import type { TypedActionWithPayload } from '@/types/redux';
import type { SearchParams } from '@/types/SaveSearch';
import type { SearchUrlParams } from '@/types/search/SearchUrlParams';

import { ConversionLabels, trackConversion } from '@/components/GoogleTag/GoogleTag';
import { type AsyncThunkConfigWithRejectValue, createAsyncThunk } from '@/redux/createAsyncThunk';
import { getAuthToken, getBidderId, isUserLoggedIn } from '@/redux/modules/account/user/user.selectors';
import { getDeployment } from '@/redux/modules/config';
import { fetchSavedSearchesIfNeeded } from '@/redux/modules/savedSearch';
import {
    type CachedRecentSearch,
    deleteRecentSearch,
    getRecentSearches,
    isInvalidFacetsError,
    isValidRecentSearch,
    postDeleteSavedSearch,
    postRecentSearch,
    postSaveSearch,
} from '@/redux/modules/search/save/saveSearch.api';
import {
    createSaveSearchKeyBySearchParams,
    getRecentSearchKeywordFromParams,
    getSaveSearchKey,
    getSaveSearchParams,
    isKeywordInRecentSearches,
    type SaveSearchParamsOrKey,
} from '@/redux/modules/search/save/saveSearch.helpers';
import {
    getIsSavedSearch,
    getSavedSearchId,
    shouldFetchRecentSearches,
} from '@/redux/modules/search/save/saveSearch.selectors';
import { SaveSearchResult } from '@/redux/modules/search/save/saveSearch.types';
import { getSearchFacets } from '@/redux/modules/searchFacetAggregates.selectors';
import { trackSearchFollowedAnalytics, trackSearchUnfollowedAnalytics } from '@/utils/amplitude/analytics';
import { getFullUrl } from '@/utils/amplitude/analytics/utils/getFullUrl';
import { getAmplitudePageNameFromPath } from '@/utils/getPageNameFromPath';
import {
    deleteRecentSearchFromCookie,
    getRecentSearchesFromCookie,
    saveRecentSearchToCookie,
} from '@/utils/recentSearchCookie';

/**
 * @deprecated Only the followed-searches list module (savedSearch.ts) dispatches this. If using redux-toolkit's createSlice, use deleteSavedSearch.pending
 */
export const DELETE_SAVE_SEARCH_REQUEST = 'la/ui/deleteSaveSearch/REQUEST';

/**
 * @deprecated Only the followed-searches list module (savedSearch.ts) dispatches this. If using redux-toolkit's createSlice, use deleteSavedSearch.fulfilled
 */
export const DELETE_SAVE_SEARCH_SUCCESS = 'la/ui/deleteSaveSearch/SUCCESS';

/**
 * @deprecated Only the followed-searches list module (savedSearch.ts) dispatches this. If using redux-toolkit's createSlice, use deleteSavedSearch.rejected
 */
export const DELETE_SAVE_SEARCH_FAIL = 'la/ui/deleteSaveSearch/FAIL';

/**
 * Payload is the `byParams` key of the search that was unfollowed.
 */
export type DeleteSaveSearchSuccessAction = TypedActionWithPayload<typeof DELETE_SAVE_SEARCH_SUCCESS, string>;

const HEADER_RECENT_SEARCHES_COUNT = 10;
const HEADER_RECENT_SEARCH_LOTS_PER_SEARCH = 1;

export type SubmitRecentSearchParams = {
    saveSearchParams: SaveSearchParamsOrKey;
    url: string;
};

export const submitRecentSearch = createAsyncThunk<CachedRecentSearch | null, SubmitRecentSearchParams>(
    'la/domain/saveSearch/submitRecentSearch',
    async ({ saveSearchParams, url }, { getState }) => {
        const state = getState();

        if (isUserLoggedIn(state)) {
            const { payload } = await postRecentSearch({
                authToken: getAuthToken(state),
                deployment: getDeployment(state),
                saveSearchParams,
                url,
            });

            return isValidRecentSearch(payload) ? payload : null;
        }

        const keyword = getRecentSearchKeywordFromParams(saveSearchParams);
        const item: CachedRecentSearch = {
            facets: { keyword },
            hash: url,
            url,
        };
        saveRecentSearchToCookie(item);
        return item;
    },
    {
        condition: ({ saveSearchParams }, { getState }) => {
            const state = getState();
            const keyword = getRecentSearchKeywordFromParams(saveSearchParams);
            return Boolean(keyword) && !isKeywordInRecentSearches(state, keyword);
        },
    }
);

export const removeRecentSearch = createAsyncThunk<string, { hash: string }>(
    'la/domain/saveSearch/removeRecentSearch',
    async ({ hash }, { getState }) => {
        const state = getState();

        if (isUserLoggedIn(state)) {
            await deleteRecentSearch({
                authToken: getAuthToken(state),
                deployment: getDeployment(state),
                hash,
            });
        } else {
            deleteRecentSearchFromCookie(hash);
        }

        return hash;
    }
);

type FetchRecentSearchesArgs = { force?: boolean };

export const fetchRecentSearches = createAsyncThunk<CachedRecentSearch[], FetchRecentSearchesArgs>(
    'la/domain/saveSearch/fetchRecentSearches',
    async (_, { getState }) => {
        const state = getState();

        if (!isUserLoggedIn(state)) {
            return getRecentSearchesFromCookie();
        }

        const response = await getRecentSearches({
            authToken: getAuthToken(state),
            bidderId: getBidderId(state),
            deployment: getDeployment(state),
            numLotsPerRecentSearch: HEADER_RECENT_SEARCH_LOTS_PER_SEARCH,
            numRecentSearchesToReturn: HEADER_RECENT_SEARCHES_COUNT,
        });

        return (response.payload ?? []).filter(isValidRecentSearch);
    },
    {
        condition: ({ force = false }, { getState }) => {
            const state = getState();
            return shouldFetchRecentSearches(state, force);
        },
    }
);

type SubmitSaveSearchParams = {
    itemId?: number;
    saveSearchParams: SaveSearchParamsOrKey;
    sendAlert: boolean;
};

type SubmitSaveSearchFailure = SaveSearchResult.Failed | SaveSearchResult.InvalidFacets;

/**
 * Fulfills when the search was followed. Rejects with the reason it was not, so callers can tell
 * the bidder about invalid facets instead of the follow button silently staying unfollowed.
 */
export const submitSaveSearch = createAsyncThunk<
    { key: string; savedSearchId: number },
    SubmitSaveSearchParams,
    AsyncThunkConfigWithRejectValue<SubmitSaveSearchFailure>
>(
    'la/domain/saveSearch/submitSaveSearch',
    async ({ itemId, saveSearchParams, sendAlert }, { dispatch, getState, rejectWithValue }) => {
        const state = getState();
        const deployment = getDeployment(state);

        let savedSearchId: number;
        try {
            const { payload } = await postSaveSearch({
                authToken: getAuthToken(state),
                deployment,
                saveSearchParams,
                sendAlert,
            });
            savedSearchId = payload;
        } catch (error) {
            return rejectWithValue(
                isInvalidFacetsError(error) ? SaveSearchResult.InvalidFacets : SaveSearchResult.Failed
            );
        }

        // Everything below runs after the save and before `fulfilled`, so a throw here would reject a
        // save the API accepted and leave the button on Follow. Keep these calls non-throwing.
        trackConversion({ label: ConversionLabels.FollowedSearch });
        dispatch(fetchSavedSearchesIfNeeded(true));
        trackSearchFollowedAnalytics({
            itemId,
            pageName: getAmplitudePageNameFromPath(window.location.pathname),
            searchKeywords: typeof saveSearchParams !== 'string' ? saveSearchParams.keyword : saveSearchParams,
            url: getFullUrl(deployment, window.location.pathname),
        });

        return { key: getSaveSearchKey(saveSearchParams), savedSearchId };
    }
);

type SubmitSaveSearchAction = Awaited<ReturnType<ReturnType<typeof submitSaveSearch>>>;

export const getSaveSearchResult = (action: SubmitSaveSearchAction): SaveSearchResult =>
    submitSaveSearch.fulfilled.match(action) ? SaveSearchResult.Saved : (action.payload ?? SaveSearchResult.Failed);

export const deleteSavedSearch = createAsyncThunk<string, SaveSearchParamsOrKey>(
    'la/domain/saveSearch/deleteSavedSearch',
    async (saveSearchParams, { dispatch, getState }) => {
        const state = getState();
        const deployment = getDeployment(state);
        const key = getSaveSearchKey(saveSearchParams);

        await postDeleteSavedSearch({
            authToken: getAuthToken(state),
            deployment,
            savedSearchId: getSavedSearchId(state, key),
        });

        // Same constraint as submitSaveSearch: a throw here would leave the button on Following.
        trackSearchUnfollowedAnalytics({
            pageName: getAmplitudePageNameFromPath(window.location.pathname),
            url: getFullUrl(deployment, window.location.pathname),
        });
        dispatch(fetchSavedSearchesIfNeeded(true));

        return key;
    }
);

export const setNewSignupSearchParams = createAsyncThunk<SearchParams, SearchParams>(
    'la/domain/saveSearch/setNewSignupSearchParams',
    async (params) => params
);

type SubmitSaveSearchAfterLoginParams = {
    browseCategoryIds?: number[];
    pathname: string;
    queryParams: SearchUrlParams;
};

/**
 * After login with saveSearch in URL: load saved searches, then only submit if this search isn't already saved.
 * Prevents duplicate saved search when user lands from alert email and logs in.
 * Resolves to null when nothing was submitted.
 */
export const submitSaveSearchAfterLoginIfNeeded = createAsyncThunk<
    SaveSearchResult | null,
    SubmitSaveSearchAfterLoginParams
>(
    'la/domain/saveSearch/submitSaveSearchAfterLoginIfNeeded',
    async ({ browseCategoryIds, pathname, queryParams }, { dispatch, getState }) => {
        const searchFacets = getSearchFacets(getState());
        const saveSearchParams = getSaveSearchParams(queryParams, pathname, false, browseCategoryIds, searchFacets);
        if (!saveSearchParams) {
            return null;
        }

        await dispatch(fetchSavedSearchesIfNeeded(true));
        if (getIsSavedSearch(getState(), createSaveSearchKeyBySearchParams(saveSearchParams))) {
            return null;
        }

        return getSaveSearchResult(await dispatch(submitSaveSearch({ saveSearchParams, sendAlert: true })));
    }
);
